/**
 * <mango-onsite> — the Onsite scope / "Onsite job complete" screen.
 * =====================================================================
 * Reached from the field brief's "Start onsite scope" (the WO is already onsite —
 * staff count + arrival were logged on the brief). Fires data-action=
 * "complete-onsite" with the typed OnsiteCompleteRequest body: scope_notes +
 * the client sign-off (a representative name OR the "unavailable" checkbox).
 * Submitting stamps onsite_completed_at — the BR#4 SLA-clock start — and advances
 * the work order to Equipment Processing.
 *
 * REVIEW & CONFIRM (J2): the amber "Submit onsite scope" button no longer fires
 * the transition directly — it opens a review modal that summarises what's being
 * submitted (staff count, client sign-off, photo count, scope notes). The modal's
 * "Confirm & submit" button is the one carrying data-action="complete-onsite", so
 * it runs the SAME submit path (buildBody → onSuccess → #/sow/…). When the scope-
 * notes field is empty, the modal shows an explicit "no onsite notes" confirmation
 * checkbox and keeps Confirm disabled until it's ticked — mirroring the existing
 * "No photos taken" advisory checkbox. The opener, Cancel, overlay-dismiss, and the
 * no-scope checkbox are all wired here via addEventListener (NOT data-action/bind),
 * so the strict CSP holds.
 *
 * PHOTOS: the before / packed-truck sections are REAL uploads — a mobile browser
 * offers camera OR photo library (no `capture` attribute, so both). Each picked
 * File posts to the work order's /photos/ endpoint (uploadWorkOrderPhoto) and the
 * returned thumbnail is appended; the ✕ removes it (deleteWorkOrderPhoto). Caps:
 * 10 before, 3 truck. A "No photos taken" checkbox keeps the step documented, not
 * hard-gated — it rides the complete-onsite submit as `no_photos:true`. All of
 * this is wired here via addEventListener (the file inputs + checkbox + ✕ are NOT
 * data-action/data-bind), so the strict CSP holds.
 *
 * fmv_2 NEVER appears: the WorkOrder type has no such field; no bind references it.
 */
import {
  getWorkOrder,
  uploadWorkOrderPhoto,
  deleteWorkOrderPhoto,
  type WorkOrder,
  type OnsiteCompleteBody,
  type WorkOrderPhoto,
} from "../api/client";
import { applyBindings, wireActions } from "../bind";
import { loadView, timeOfDay, setText } from "./shared";

const VIEW_URL = new URL("../../views/onsite.html", import.meta.url).href;

/** Per-section upload caps (before-collection vs packed-truck). `facility` is
 *  never captured on this screen (it's a SOW-screen kind), so its cap is 0. */
const PHOTO_CAPS: Record<WorkOrderPhoto["kind"], number> = { before: 10, truck: 3, facility: 0 };

export class MangoOnsite extends HTMLElement {
  static observedAttributes = ["data-short-name"];
  private disposeActions: (() => void) | null = null;
  private timer: number | null = null;
  private job: WorkOrder | null = null;
  // Photo lightbox state: the URLs currently loaded into the track + the active index.
  private lightboxUrls: string[] = [];
  private lightboxIndex = 0;
  private touchStartX: number | null = null;
  private onLightboxKey: ((e: KeyboardEvent) => void) | null = null;

  async connectedCallback(): Promise<void> {
    this.innerHTML = await loadView(VIEW_URL);

    this.disposeActions = wireActions(this, {
      // Fired from the review modal's "Confirm & submit" (data-action="complete-onsite").
      // Same body the form always sent — the review/confirm gate is pure UI, no new fields.
      getBody: (ev) => {
        if (ev.action !== "complete-onsite") return undefined;
        const built = this.buildBody();
        if (!built) {
          this.setStatus("Add the client sign-off (a representative name, or mark unavailable).");
          return false;
        }
        // Safety net: if scope notes are empty, the no-scope confirmation must be
        // ticked (the Confirm button is also kept disabled until then — belt & braces).
        if (!this.scopeNotes() && !this.noScopeConfirmed()) return false;
        return built;
      },
      onSuccess: (ev) => {
        // Onsite complete → Equipment Processing: continue to the SOW screen.
        this.closeReviewModal();
        window.location.hash = `#/sow/${encodeURIComponent(ev.id)}`;
      },
      onError: (ev, error) => {
        console.error(`[mango-onsite] complete-onsite failed for ${ev.id}`, error);
        this.setStatus("Could not submit onsite scope. Please retry.");
      },
    });

    this.wireForm();
    this.wirePhotos();
    this.wireReviewModal();
    this.wireLightbox();
    this.startTimer();
    await this.load();
  }

  disconnectedCallback(): void {
    this.disposeActions?.();
    this.disposeActions = null;
    if (this.onLightboxKey) {
      document.removeEventListener("keydown", this.onLightboxKey);
      this.onLightboxKey = null;
    }
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  attributeChangedCallback(): void {
    if (this.isConnected) void this.load();
  }

  private get shortName(): string {
    return this.getAttribute("data-short-name") ?? "";
  }

  private setStatus(message: string): void {
    setText(this, "[data-status]", message);
  }

  /** A simple session timer (m:ss) in the amber header, like the prototype. */
  private startTimer(): void {
    const el = this.querySelector<HTMLElement>("[data-onsite-timer]");
    if (!el) return;
    const start = Date.now();
    const tick = (): void => {
      const s = Math.floor((Date.now() - start) / 1000);
      el.textContent = `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
    };
    tick();
    this.timer = window.setInterval(tick, 1000);
  }

  /** Build the typed body (scope_notes + client sign-off + no-photos flag +
   *  onsite_equipment_description), or null if invalid.
   *  Returns null when: sign-off is missing, OR equipment description is empty.
   *  staff_count is NOT re-sent — it was captured at arrival and is already on the WO. */
  private buildBody(): OnsiteCompleteBody | null {
    const name = this.querySelector<HTMLInputElement>("[data-signoff-name]")?.value.trim() ?? "";
    const unavailable = this.querySelector<HTMLInputElement>("[data-signoff-unavailable]")?.checked ?? false;
    if (!name && !unavailable) return null;

    const equipmentDescription =
      this.querySelector<HTMLTextAreaElement>("[data-equipment-description]")?.value.trim() ?? "";
    if (!equipmentDescription) return null;

    const notes = this.querySelector<HTMLTextAreaElement>("[data-scope-notes]")?.value.trim() ?? "";
    const noPhotos = this.querySelector<HTMLInputElement>("[data-no-photos]")?.checked ?? false;

    const body: OnsiteCompleteBody = { client_signoff_unavailable: unavailable };
    if (notes) body.scope_notes = notes;
    if (name) body.client_signoff_name = name;
    if (noPhotos) body.no_photos = true;
    (body as Record<string, unknown>).onsite_equipment_description = equipmentDescription;
    return body;
  }

  private wireForm(): void {
    const update = (): void => {
      const name = this.querySelector<HTMLInputElement>("[data-signoff-name]")?.value.trim() ?? "";
      const unavailable = this.querySelector<HTMLInputElement>("[data-signoff-unavailable]")?.checked ?? false;
      const preview = this.querySelector<HTMLElement>("[data-signoff-preview]");
      if (preview) preview.textContent = name;
      const btn = this.querySelector<HTMLButtonElement>("[data-submit-btn]");
      if (btn) btn.disabled = !(name.length > 0 || unavailable);
    };
    this.querySelector<HTMLInputElement>("[data-signoff-name]")?.addEventListener("input", update);
    this.querySelector<HTMLInputElement>("[data-signoff-unavailable]")?.addEventListener("change", update);
  }

  // ── Review & confirm modal (J2) ──────────────────────────────────────────────
  /** Current trimmed scope-expansion notes (the source of truth for the empty check). */
  private scopeNotes(): string {
    return this.querySelector<HTMLTextAreaElement>("[data-scope-notes]")?.value.trim() ?? "";
  }

  /** Whether the "no onsite notes" confirmation is ticked (only relevant when notes empty). */
  private noScopeConfirmed(): boolean {
    return this.querySelector<HTMLInputElement>("[data-review-noscope]")?.checked ?? false;
  }

  /** The client sign-off summary line: the typed name, or the documented-unavailable note. */
  private signoffSummary(): string {
    const name = this.querySelector<HTMLInputElement>("[data-signoff-name]")?.value.trim() ?? "";
    if (name) return name;
    const unavailable = this.querySelector<HTMLInputElement>("[data-signoff-unavailable]")?.checked ?? false;
    return unavailable ? "Representative unavailable — documented in notes" : "—";
  }

  /** Wire the opener (populate + show), Cancel / overlay-dismiss, and the empty-scope
   *  confirmation checkbox that gates Confirm. CSP-safe (addEventListener only). */
  private wireReviewModal(): void {
    // "Submit onsite scope" → run the form's own sign-off validation, then open the
    // review modal. (The button has no data-action, so it never fires the transition.)
    this.querySelector<HTMLElement>("[data-review-open]")?.addEventListener("click", () => {
      if (!this.buildBody()) {
        this.setStatus("Add the client sign-off (a representative name, or mark unavailable).");
        return;
      }
      this.openReviewModal();
    });

    // Cancel + click-outside close the modal (returns to the form, nothing submitted).
    this.querySelector<HTMLElement>("[data-review-cancel]")?.addEventListener("click", () => this.closeReviewModal());
    const overlay = this.querySelector<HTMLElement>("[data-review-modal]");
    overlay?.addEventListener("click", (e) => {
      if (e.target === overlay) this.closeReviewModal();
    });

    // Empty-scope confirmation toggles Confirm's enabled state (mirrors the no-photos cb).
    this.querySelector<HTMLInputElement>("[data-review-noscope]")?.addEventListener("change", () =>
      this.refreshReviewConfirm(),
    );
  }

  /** Fill the review summary, reveal the no-scope confirmation iff notes are empty,
   *  set the action's data-id, and show the modal. */
  private openReviewModal(): void {
    const modal = this.querySelector<HTMLElement>("[data-review-modal]");
    if (!modal) return;
    // The complete-onsite action resolves its target from the overlay's data-id.
    modal.dataset.id = this.shortName;

    // Summary rows (short_name is data-bound; fill the rest from the live form / WO).
    setText(this, "[data-review-staff]", this.job?.staff_count == null ? "—" : String(this.job.staff_count));
    setText(this, "[data-review-signoff]", this.signoffSummary());
    const photos = this.photoCount("before") + this.photoCount("truck");
    const noPhotos = this.querySelector<HTMLInputElement>("[data-no-photos]")?.checked ?? false;
    setText(
      this,
      "[data-review-photos]",
      noPhotos ? "No photos taken" : `${photos} photo${photos === 1 ? "" : "s"} attached`,
    );

    // Scope notes: show the typed notes, or the "No scope changes" empty-state.
    const notes = this.scopeNotes();
    setText(this, "[data-review-notes]", notes || "No scope changes");

    // Empty-scope confirmation: required only when notes are empty (reset each open).
    const noScopeRow = this.querySelector<HTMLElement>("[data-review-noscope-row]");
    const noScopeCb = this.querySelector<HTMLInputElement>("[data-review-noscope]");
    if (noScopeCb) noScopeCb.checked = false;
    if (noScopeRow) noScopeRow.style.display = notes ? "none" : "";

    this.refreshReviewConfirm();
    modal.classList.add("show");
    (this.querySelector<HTMLElement>("[data-review-cancel]"))?.focus();
  }

  private closeReviewModal(): void {
    this.querySelector<HTMLElement>("[data-review-modal]")?.classList.remove("show");
  }

  /** Confirm is enabled unless scope notes are empty AND the no-scope cb is unticked. */
  private refreshReviewConfirm(): void {
    const confirm = this.querySelector<HTMLButtonElement>("[data-review-confirm]");
    if (confirm) confirm.disabled = !this.scopeNotes() && !this.noScopeConfirmed();
  }

  // ── Onsite photos (real uploads) ─────────────────────────────────────────────
  /** Wire each section's add tile (opens the file input), the file input change
   *  (upload each picked File), and a delegated ✕ click (delete). CSP-safe. */
  private wirePhotos(): void {
    // Add tile → open the matching hidden file input.
    this.querySelectorAll<HTMLElement>("[data-photo-add]").forEach((add) => {
      const kind = add.dataset.photoAdd as WorkOrderPhoto["kind"];
      add.addEventListener("click", () => this.photoInput(kind)?.click());
    });
    // File input change → upload each selected file.
    this.querySelectorAll<HTMLInputElement>("[data-photo-input]").forEach((input) => {
      const kind = input.dataset.photoKind as WorkOrderPhoto["kind"];
      input.addEventListener("change", () => {
        const files = input.files ? Array.from(input.files) : [];
        input.value = ""; // allow re-picking the same file later
        void this.uploadPhotos(kind, files);
      });
    });
    // Delegated ✕ on a thumbnail → delete that photo.
    this.addEventListener("click", (e) => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>("[data-photo-remove]");
      if (btn) void this.removePhoto(btn);
    });
  }

  private photoInput(kind: WorkOrderPhoto["kind"]): HTMLInputElement | null {
    return this.querySelector<HTMLInputElement>(`[data-photo-input][data-photo-kind="${kind}"]`);
  }

  private photoGrid(kind: WorkOrderPhoto["kind"]): HTMLElement | null {
    return this.querySelector<HTMLElement>(`[data-photo-grid="${kind}"]`);
  }

  private addTile(kind: WorkOrderPhoto["kind"]): HTMLElement | null {
    return this.querySelector<HTMLElement>(`[data-photo-add="${kind}"]`);
  }

  /** Current uploaded thumbnail count in a section (excludes the add tile). */
  private photoCount(kind: WorkOrderPhoto["kind"]): number {
    return this.photoGrid(kind)?.querySelectorAll("[data-photo-id]").length ?? 0;
  }

  private setPhotoStatus(kind: WorkOrderPhoto["kind"], message: string, isError = false): void {
    const el = this.querySelector<HTMLElement>(`[data-photo-status="${kind}"]`);
    if (!el) return;
    el.textContent = message;
    el.classList.toggle("err", isError);
  }

  /** Enable/disable the add tile once a section hits its cap. */
  private refreshAddTile(kind: WorkOrderPhoto["kind"]): void {
    const add = this.addTile(kind);
    if (add) (add as HTMLButtonElement).disabled = this.photoCount(kind) >= PHOTO_CAPS[kind];
  }

  /** Render a saved photo as a thumbnail tile inserted before the add tile. */
  private appendThumb(photo: WorkOrderPhoto): void {
    const grid = this.photoGrid(photo.kind);
    if (!grid) return;
    const tile = document.createElement("div");
    tile.className = "photo-slot filled photo-thumb";
    tile.dataset.photoId = String(photo.id);
    tile.dataset.photoUrl = photo.url; // read back by the lightbox to build its track

    const img = document.createElement("img");
    img.src = photo.url;
    img.alt = photo.kind === "before" ? "Before-collection photo" : "Packed truck photo";
    // Tapping the photo opens the swipeable lightbox at this tile (the ✕ button has its
    // own delegated handler; the open handler ignores clicks that land on it).
    img.style.cursor = "zoom-in";
    img.addEventListener("click", () => this.openLightbox(tile));
    tile.appendChild(img);

    const remove = document.createElement("button");
    remove.type = "button";
    remove.className = "photo-remove";
    remove.dataset.photoRemove = "";
    remove.setAttribute("aria-label", "Remove photo");
    const x = document.createElement("i");
    x.className = "ti ti-x";
    remove.appendChild(x);
    tile.appendChild(remove);

    grid.insertBefore(tile, this.addTile(photo.kind));
  }

  /** Upload each picked file (respecting the per-section cap), appending thumbs. */
  private async uploadPhotos(kind: WorkOrderPhoto["kind"], files: File[]): Promise<void> {
    if (!files.length) return;
    const cap = PHOTO_CAPS[kind];
    let room = cap - this.photoCount(kind);
    if (room <= 0) {
      this.setPhotoStatus(kind, `Maximum ${cap} photos reached.`, true);
      return;
    }
    const queued = files.slice(0, room);
    const skipped = files.length - queued.length;

    // A transient "uploading" placeholder tile (no image — blob: previews are
    // blocked by the CSP, so we render the real thumbnail from the server URL).
    const add = this.addTile(kind);
    let done = 0;
    for (const file of queued) {
      this.setPhotoStatus(kind, `Uploading ${done + 1} of ${queued.length}…`);
      const ph = document.createElement("div");
      ph.className = "photo-slot photo-thumb uploading";
      const spinner = document.createElement("i");
      spinner.className = "ti ti-refresh";
      ph.appendChild(spinner);
      this.photoGrid(kind)?.insertBefore(ph, add);
      try {
        const photo = await uploadWorkOrderPhoto(this.shortName, kind, file);
        ph.remove();
        this.appendThumb(photo);
        done++;
      } catch (error) {
        console.error(`[mango-onsite] photo upload failed (${kind})`, error);
        ph.remove();
        this.setPhotoStatus(kind, "Upload failed. Please retry.", true);
        this.refreshAddTile(kind);
        return;
      }
    }
    this.refreshAddTile(kind);
    this.setPhotoStatus(
      kind,
      skipped > 0 ? `Uploaded ${done}. ${skipped} skipped — max ${cap}.` : "",
      skipped > 0,
    );
  }

  /** Delete the photo for a ✕ button, then remove its tile. */
  private async removePhoto(button: HTMLElement): Promise<void> {
    const tile = button.closest<HTMLElement>("[data-photo-id]");
    const grid = button.closest<HTMLElement>("[data-photo-grid]");
    const id = tile?.dataset.photoId;
    const kind = grid?.dataset.photoGrid as WorkOrderPhoto["kind"] | undefined;
    if (!tile || !id || !kind) return;
    button.setAttribute("aria-busy", "true");
    try {
      await deleteWorkOrderPhoto(this.shortName, Number(id));
      tile.remove();
      this.refreshAddTile(kind);
      this.setPhotoStatus(kind, "");
    } catch (error) {
      console.error("[mango-onsite] photo delete failed", error);
      button.removeAttribute("aria-busy");
      this.setPhotoStatus(kind, "Could not remove the photo. Please retry.", true);
    }
  }

  // ── Photo lightbox (swipe through all onsite photos) ─────────────────────────
  /** Wire the lightbox once: close (button + backdrop), prev/next, and touch-swipe.
   *  Keyboard (←/→/Esc) is bound only while the lightbox is open. CSP-safe. */
  private wireLightbox(): void {
    const overlay = this.querySelector<HTMLElement>("[data-photo-lightbox]");
    if (!overlay) return;

    this.querySelector<HTMLElement>("[data-lightbox-close]")?.addEventListener("click", () => this.closeLightbox());
    // Backdrop dismiss: a click on the overlay itself (outside the stage) closes it.
    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) this.closeLightbox();
    });
    this.querySelector<HTMLElement>("[data-lightbox-prev]")?.addEventListener("click", () => this.lightboxStep(-1));
    this.querySelector<HTMLElement>("[data-lightbox-next]")?.addEventListener("click", () => this.lightboxStep(1));

    // Touch swipe — plain start/end deltas, no dependency. >40px horizontal = a step.
    const track = this.querySelector<HTMLElement>("[data-lightbox-track]");
    track?.addEventListener("touchstart", (e) => {
      this.touchStartX = e.changedTouches[0]?.clientX ?? null;
    }, { passive: true });
    track?.addEventListener("touchend", (e) => {
      if (this.touchStartX == null) return;
      const dx = (e.changedTouches[0]?.clientX ?? this.touchStartX) - this.touchStartX;
      this.touchStartX = null;
      if (Math.abs(dx) > 40) this.lightboxStep(dx < 0 ? 1 : -1);
    }, { passive: true });
  }

  /** Open the lightbox showing `tile`'s photo, with a track of every uploaded photo
   *  for this work order (before + truck, in DOM order). */
  private openLightbox(tile: HTMLElement): void {
    const overlay = this.querySelector<HTMLElement>("[data-photo-lightbox]");
    const track = this.querySelector<HTMLElement>("[data-lightbox-track]");
    if (!overlay || !track) return;

    // Collect every uploaded thumbnail's URL across both sections (skip the add tile /
    // any in-flight "uploading" placeholders, which carry no data-photo-url).
    const tiles = Array.from(this.querySelectorAll<HTMLElement>("[data-photo-id][data-photo-url]"));
    this.lightboxUrls = tiles.map((t) => t.dataset.photoUrl ?? "");
    this.lightboxIndex = Math.max(0, tiles.indexOf(tile));

    track.replaceChildren(
      ...this.lightboxUrls.map((url) => {
        const img = document.createElement("img");
        img.src = url;
        img.alt = "Onsite photo";
        img.draggable = false;
        return img;
      }),
    );

    this.renderLightbox();
    overlay.classList.add("show");
    this.querySelector<HTMLElement>("[data-lightbox-close]")?.focus();

    // Keyboard control only while open.
    this.onLightboxKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") this.closeLightbox();
      else if (e.key === "ArrowLeft") this.lightboxStep(-1);
      else if (e.key === "ArrowRight") this.lightboxStep(1);
    };
    document.addEventListener("keydown", this.onLightboxKey);
  }

  private closeLightbox(): void {
    this.querySelector<HTMLElement>("[data-photo-lightbox]")?.classList.remove("show");
    if (this.onLightboxKey) {
      document.removeEventListener("keydown", this.onLightboxKey);
      this.onLightboxKey = null;
    }
  }

  /** Move by ±1, clamped to the ends. */
  private lightboxStep(delta: number): void {
    const next = this.lightboxIndex + delta;
    if (next < 0 || next >= this.lightboxUrls.length) return;
    this.lightboxIndex = next;
    this.renderLightbox();
  }

  /** Slide the track to the active index + update the counter and prev/next disabled state. */
  private renderLightbox(): void {
    const track = this.querySelector<HTMLElement>("[data-lightbox-track]");
    if (track) track.style.transform = `translateX(-${this.lightboxIndex * 100}%)`;
    setText(this, "[data-lightbox-count]",
      this.lightboxUrls.length ? `${this.lightboxIndex + 1} / ${this.lightboxUrls.length}` : "");
    const prev = this.querySelector<HTMLButtonElement>("[data-lightbox-prev]");
    const next = this.querySelector<HTMLButtonElement>("[data-lightbox-next]");
    if (prev) prev.disabled = this.lightboxIndex <= 0;
    if (next) next.disabled = this.lightboxIndex >= this.lightboxUrls.length - 1;
  }

  /** Paint the saved photos + reflect the no-photos flag from the loaded WO. */
  private renderPhotos(job: WorkOrder): void {
    // photos / onsite_no_photos are added to the contract by the backend; until
    // the generated schema picks them up, read them off the loaded object (like
    // the overages pattern in mango-sow).
    const rec = job as unknown as Record<string, unknown>;
    const photos = Array.isArray(rec.photos) ? (rec.photos as WorkOrderPhoto[]) : [];
    for (const kind of ["before", "truck"] as const) {
      // Clear any previously-painted thumbnails (re-render is idempotent).
      this.photoGrid(kind)?.querySelectorAll("[data-photo-id]").forEach((t) => t.remove());
    }
    for (const photo of photos) {
      if (photo && (photo.kind === "before" || photo.kind === "truck")) this.appendThumb(photo);
    }
    this.refreshAddTile("before");
    this.refreshAddTile("truck");

    const noPhotos = this.querySelector<HTMLInputElement>("[data-no-photos]");
    if (noPhotos) noPhotos.checked = rec.onsite_no_photos === true;
  }

  private async load(): Promise<void> {
    const id = this.shortName;
    if (!id) {
      this.setStatus("No work order specified.");
      return;
    }
    this.setStatus("Loading work order…");
    try {
      this.job = await getWorkOrder(id);
    } catch (error) {
      console.error("[mango-onsite] failed to load", error);
      this.setStatus("Could not load this work order.");
      return;
    }
    applyBindings(this, { job: this.job });
    // Job summary: arrival as time-of-day; staff with a fallback.
    this.querySelectorAll<HTMLElement>('[data-bind="job.onsite_arrived_at"]').forEach(
      (el) => (el.textContent = timeOfDay(this.job?.onsite_arrived_at)),
    );
    setText(this, '[data-bind="job.staff_count"]',
      this.job.staff_count == null ? "—" : String(this.job.staff_count));
    // Job-scope estimates (client booking; "~" = approximate).
    setText(this, '[data-bind="job.estimated_device_count"]',
      this.job.estimated_device_count == null ? "—" : `~${this.job.estimated_device_count}`);
    setText(this, '[data-bind="job.estimated_ewaste_lbs"]',
      this.job.estimated_ewaste_lbs == null ? "—" : `~${this.job.estimated_ewaste_lbs} lbs`);
    this.renderPhotos(this.job);
    this.setStatus("");
  }
}

export function registerMangoOnsite(): void {
  if (!customElements.get("mango-onsite")) {
    customElements.define("mango-onsite", MangoOnsite);
  }
}
